import React, { useEffect, useState, useRef, useId } from "react";
import { X } from "lucide-react";
import "./PaginatedMultiSelect.css";

const PaginatedMultiSelect = ({
  api,
  GetPaginatedData,
  PostPaginatedData,
  method = "GET",
  getOptionLabel,
  onSelect,
  value = [],
  placeholder = "Search...",
  disabled = false,
  filterFn,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedValues, setSelectedValues] = useState(value || []);
  const [isFocused, setIsFocused] = useState(false);

  const wrapperRef = useRef(null);
  const reactId = useId();

  /* CLOSE ON OUTSIDE CLICK */
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setDropdownOpen(false);
        setIsFocused(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClick);
    }

    return () => document.removeEventListener("mousedown", handleClick);
  }, [dropdownOpen]);

  /* FETCH DATA */
  useEffect(() => {
    if (!dropdownOpen) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        let res;

        if (method === "POST" && PostPaginatedData) {
          res = await PostPaginatedData(api, { page, search: searchTerm });
        } else {
          res = await GetPaginatedData(api, { page, search: searchTerm });
        }

        let data = res.data || [];
        if (filterFn) data = data.filter(filterFn);

        setOptions(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [api, page, searchTerm, dropdownOpen]);

  useEffect(() => {
    setSelectedValues(value || []);
  }, [value]);

  const toggleSelect = (opt) => {
    const exists = selectedValues.find((v) => v.id === opt.id);

    const updated = exists
      ? selectedValues.filter((v) => v.id !== opt.id)
      : [...selectedValues, opt];

    setSelectedValues(updated);
    onSelect(updated);

    setSearchTerm("");
  };

  return (
    <div className="pss-multi-select" ref={wrapperRef}>
      
      {/* INPUT WRAPPER */}
      <div className="pss-multi-wrapper">
        
        <div className="pss-multi-tags">
          {selectedValues.map((item) => (
            <span key={item.id} className="pss-multi-tag">
              {getOptionLabel(item)}
              <X size={12} onClick={() => toggleSelect(item)} />
            </span>
          ))}
        </div>

        <input
          id={reactId}
          className="pss-multi-input"
          type="text"
          disabled={disabled}
          value={searchTerm}
          onFocus={() => {
            setDropdownOpen(true);
            setIsFocused(true);
          }}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
        />

        <label
          className={`pss-multi-label ${
            isFocused || searchTerm || selectedValues.length ? "active" : ""
          }`}
        >
          {placeholder}
        </label>

        {(selectedValues.length > 0 || searchTerm) && (
          <span
            className="pss-multi-clear"
            onClick={() => {
              setSearchTerm("");
              setSelectedValues([]);
              onSelect([]);
            }}
          >
            <X size={16} />
          </span>
        )}
      </div>

      {/* DROPDOWN (FIXED POSITION) */}
      {dropdownOpen && (
        <div
          className="pss-multi-dropdown"
          onMouseDown={(e) => e.preventDefault()}
        >
          {loading && <div className="pss-multi-loading">Loading…</div>}

          {!loading && options.length === 0 && (
            <div className="pss-multi-no-results">No results</div>
          )}

          {options.map((opt, idx) => (
            <button
              key={opt.id || idx}
              type="button"
              className={`pss-multi-option ${
                selectedValues.some((v) => v.id === opt.id)
                  ? "selected"
                  : ""
              }`}
              onClick={() => toggleSelect(opt)}
            >
              {getOptionLabel(opt)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PaginatedMultiSelect;